/**
 * ============================================
 * BACKEND API SERVICE
 * ============================================
 * 
 * Single service that fetches ALL data from the backend.
 * No direct AWS calls - everything goes through our backend.
 */

import { BACKEND_URL } from '@/config';

/**
 * Generic fetch from backend with error handling
 */
async function fetchBackend<T>(endpoint: string): Promise<T> {
  const url = `${BACKEND_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Backend error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
}

// ============================================
// HEALTH & STATUS
// ============================================

export async function checkBackendHealth(): Promise<{
  status: string;
  version: string;
  monitor_running: boolean;
}> {
  return fetchBackend('/api/v1/health');
}

export async function getBackendStatus(): Promise<{
  status: string;
  version: string;
  monitor_running: boolean;
  environments_loaded: number;
  uptime_seconds: number;
  last_check?: string;
}> {
  return fetchBackend('/api/v1/status');
}

// ============================================
// ENVIRONMENTS
// ============================================

export interface EnvironmentSummary {
  id: string;
  name: string;
  short_name: string;
  color: string;
  is_active: boolean;
  monitor_enabled: boolean;
}

export async function getEnvironments(): Promise<{
  environments: EnvironmentSummary[];
}> {
  return fetchBackend('/api/v1/environments');
}

export interface ServiceStatus {
  service_id: string;
  environment_id: string;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  message: string;
  response_time_ms?: number;
  details?: Record<string, unknown>;
  checked_at?: string;
}

export interface EnvironmentAllData {
  environment: string;
  health: {
    services: Record<string, ServiceStatus>;
    overall_status: string;
    healthy_count: number;
    total_count: number;
  };
  infrastructure: {
    ec2_count?: number;
    s3_objects?: number;
    lambda_functions?: number;
    asg_instances?: number;
  };
  players: {
    total_players: number;
    platform_breakdown: Record<string, number>;
    platform_combinations?: Record<string, number>;
    cluster_name?: string;
  };
  remote_config: Array<{
    version: string;
    url: string;
    forceUpdate: boolean;
    environment: string;
  }>;
  aws_org_name?: string;
  // Backend sets this to true when the per-environment cache hasn't been
  // populated yet (monitor still warming). Frontend keeps the loading
  // spinner visible and retries fast rather than render an empty stub.
  loading?: boolean;
}

export async function getEnvironmentAll(envId: string): Promise<EnvironmentAllData> {
  return fetchBackend(`/api/v1/environments/${envId}/all`);
}

export async function getEnvironmentHealth(envId: string): Promise<{
  services: Record<string, ServiceStatus>;
  overall_status: string;
  healthy_count: number;
  total_count: number;
}> {
  return fetchBackend(`/api/v1/environments/${envId}/health`);
}

export async function getEnvironmentInfrastructure(envId: string): Promise<{
  ec2_count?: number;
  s3_objects?: number;
  lambda_functions?: number;
  asg_instances?: number;
}> {
  return fetchBackend(`/api/v1/environments/${envId}/infrastructure`);
}

export async function getEnvironmentPlayers(envId: string): Promise<{
  total_players: number;
  platform_breakdown: Record<string, number>;
}> {
  return fetchBackend(`/api/v1/environments/${envId}/players`);
}

// ============================================
// ALERTS
// ============================================

export interface BackendAlert {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  environment_id: string;
  service_id?: string;
  timestamp: string;
  acknowledged: boolean;
}

export async function getAlerts(): Promise<{
  active: BackendAlert[];
  has_critical: boolean;
}> {
  return fetchBackend('/api/v1/alerts');
}

export async function acknowledgeAlerts(): Promise<{ success: boolean }> {
  const response = await fetch(`${BACKEND_URL}/api/v1/alerts/acknowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
}

export async function clearAlerts(): Promise<{ success: boolean }> {
  const response = await fetch(`${BACKEND_URL}/api/v1/alerts/clear`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
}

export async function simulateAlert(
  environment: string,
  service: string,
  playSound = true
): Promise<{ success: boolean }> {
  const response = await fetch(`${BACKEND_URL}/api/v1/alerts/debug/simulate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ environment, service, play_sound: playSound }),
  });
  return response.json();
}

// ============================================
// METRICS
// ============================================

export interface Certificate {
  domain: string;
  status: string;  // VALID, ISSUED, EXPIRED, EXPIRING_SOON, CRITICAL, VALIDATION_TIMED_OUT
  original_status?: string;
  not_after?: string;
  days_remaining?: number;
  type?: string;
}

export interface DomainPurchased {
  domain_name: string;
  auto_renew: boolean;
  transfer_lock: boolean;
  expiry: string;
}

export async function getCertificates(): Promise<{
  certificates: Certificate[];
  domains_purchased: DomainPurchased[];
  error?: string;
}> {
  return fetchBackend('/api/v1/metrics/certificates');
}

export async function getPrometheusMetrics(
  query: string,
  duration = '1h',
  step = '60s'
): Promise<{
  query: string;
  data: unknown[];
  error?: string;
}> {
  const params = new URLSearchParams({ query, duration, step });
  return fetchBackend(`/api/v1/metrics/prometheus?${params}`);
}

export interface CCUMetrics {
  current_ccu: number;
  ccu_history: Array<{
    timestamp: string;
    value: number;
  }>;
  duration: string;
  error?: string;
}

export async function getCCUMetrics(duration = '1h'): Promise<CCUMetrics> {
  const params = new URLSearchParams({ duration });
  return fetchBackend(`/api/v1/metrics/ccu?${params}`);
}

export async function getRemoteConfig(): Promise<{
  endpoints: Array<{
    version: string;
    url: string;
    forceUpdate: boolean;
    environment: string;
  }>;
}> {
  return fetchBackend('/api/v1/metrics/remote-config');
}

// ============================================
// PUSH NOTIFICATIONS
// ============================================

export async function getVapidPublicKey(): Promise<{
  publicKey: string | null;
  configured: boolean;
}> {
  try {
    return await fetchBackend('/api/v1/alerts/push/vapid-key');
  } catch {
    return { publicKey: null, configured: false };
  }
}

export async function subscribeToPush(subscription: PushSubscription): Promise<{
  subscriptionId: string;
}> {
  const response = await fetch(`${BACKEND_URL}/api/v1/alerts/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subscription.toJSON()),
  });
  return response.json();
}
